import { AviationHullForm, AviationLiabilitiesForm, CommercialGeneralLiabilityForm } from './base-coverage.model'

/** Accordion value of the "General" panel shown above the sub-coverage panels. */
export const GENERAL_PANEL = 'general'

/**
 * Names of a coverage form's sub-coverage groups, i.e. every control key except the General group.
 */
export type SubCoverageKey<T> = Exclude<keyof T & string, typeof GENERAL_PANEL>

/**
 * One sub-coverage accordion panel of a coverage form.
 * `key` is the form control name, the accordion panel value and the nav section `subValue`.
 */
export interface SubCoveragePanel<K extends string = string> {
  /** Form control name of the sub-coverage; also the accordion value and nav `subValue` */
  readonly key: K
  /** Panel heading, also used in the nav section label */
  readonly label: string
  /** Prefix for the panel's data-test attributes (`-panel`, `-header`, `-content`, `-sub-form`) */
  readonly dataTest: string
}

/** Displayed sub-coverage panels of Aviation Hull, in display order. */
export const AVIATION_HULL_PANELS: readonly SubCoveragePanel<SubCoverageKey<AviationHullForm>>[] = [
  { key: 'hullAllRisk', label: 'Hull All Risk', dataTest: 'hull-all-risk' },
  { key: 'hullWarAndAlliedPerils', label: 'Hull War and Allied Perils', dataTest: 'hull-war-and-allied-perils' },
  { key: 'sparesLocation', label: 'Spares Location', dataTest: 'spares-location' },
  { key: 'sparesLocationExcessOnly', label: 'Spares Location (Excess Only)', dataTest: 'spares-location-excess-only' },
  { key: 'sparesTransport', label: 'Spares Transport', dataTest: 'spares-transport' },
  { key: 'sparesTransportExcessOnly', label: 'Spares Transport (Excess Only)', dataTest: 'spares-transport-excess-only' },
  { key: 'sparesWar', label: 'Spares War', dataTest: 'spares-war' },
  { key: 'hullDeductible', label: 'Hull Deductible', dataTest: 'hull-deductible' }
]

/** Sub-coverage panels of Aviation Liabilities, in display order. */
export const AVIATION_LIABILITIES_PANELS: readonly SubCoveragePanel<SubCoverageKey<AviationLiabilitiesForm>>[] = [
  {
    key: 'aviationCombinedThirdPartyAndPassengerLiability',
    label: 'Aviation Combined Third Party & Passenger Liability',
    dataTest: 'combined-third-party-passenger-liability'
  },
  { key: 'aviationThirdPartyLiability', label: 'Aviation Third Party Liability', dataTest: 'third-party-liability' },
  { key: 'aviationPassengerLiability', label: 'Aviation Passenger Liability', dataTest: 'passenger-liability' },
  { key: 'aviationFreightLiability', label: 'Aviation Freight Liability', dataTest: 'freight-liability' },
  { key: 'aviationProductsLiability', label: 'Aviation Products Liability', dataTest: 'products-liability' },
  { key: 'aviationHangarkeepersLiability', label: 'Aviation Hangarkeepers Liability', dataTest: 'hangarkeepers-liability' },
  { key: 'aviationLandingGroundLiability', label: 'Aviation Landing Ground Liability', dataTest: 'landing-ground-liability' },
  {
    key: 'aviationMotorVehicleLiabilityRegistered',
    label: 'Aviation Motor Vehicle Liability (Registered Excess Only)',
    dataTest: 'motor-vehicle-liability'
  },
  {
    key: 'aviationMotorVehicleLiabilityUnregistered',
    label: 'Aviation Motor Vehicle Liability (Non-Registered)',
    dataTest: 'motor-vehicle-unregistered-liability'
  },
  {
    key: 'aviationExtendedGeneralLiabilityAvn59',
    label: 'Aviation Extended General Liability (AVN59)',
    dataTest: 'extended-general-avn-59-liability'
  },
  { key: 'aviationThirdPartyWarLiability', label: 'Aviation Third Party War Liability', dataTest: 'third-party-war-liability' },
  {
    key: 'aviationThirdPartyWarExcessLiability',
    label: 'Aviation Third Party War Excess Liability',
    dataTest: 'third-party-war-excess-liability'
  },
  { key: 'nonAviationExcessLiability', label: 'Non-Aviation Excess Liability', dataTest: 'non-aviation-excess-liability' }
]

/** Sub-coverage panels of Commercial General Liability, in display order. */
export const COMMERCIAL_GENERAL_LIABILITY_PANELS: readonly SubCoveragePanel<SubCoverageKey<CommercialGeneralLiabilityForm>>[] = [
  {
    key: 'generalThirdPartyLiabilityNonAviationPrimary',
    label: 'General Third Party Liability (Non-Aviation Primary)',
    dataTest: 'general-third-party-liability-non-aviation-primary'
  }
]
